import React, { useState, useEffect, useReducer, useCallback } from 'react';

import { TablePagination } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import Skeleton from '@material-ui/lab/Skeleton';
import { disableAgendaQueueAction, enableAgendaQueueAction } from 'actions/addConsumeAgendaActions';
import {
  addAgendasAction,
  updateAgendaAction,
  addNewAgendaAction,
  removeAgendaAction,
  updateAgendasAction,
} from 'actions/agenda';
import { useMatchedRoute } from 'components/hooks/useMatchedRoute';
import usePrevious from 'components/hooks/usePrevious';
import DOMPurify from 'dompurify';
import parse from 'html-react-parser';
import PropType from 'prop-types';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { withRouter } from 'react-router-dom';
import agendasReducer from 'reducers/agenda';
import NoActionPointsView from 'screens/common/sidepanel/CheckIn/NoActionPointsView';
import { selectLoggedInEmployeeData } from 'selectors/loggedInEmployee';
import styled, { css } from 'styled-components';
import {
  StyledTypography,
  StyledButton,
  MeshOutlineButtonCSS,
  StyledIconButton,
  SpaceBetweenContainer,
  StyledTooltip,
  StyledGrid,
  VerticalFlexContainer,
  FlexContainer,
} from 'styles/common';

import NotAvailable from 'assets/images/NotAvailable.png';

import { TASK_TYPES_2, APP_SOURCE_CONTAINERS, AGENDA_STATUSES } from 'constants/ProjectConstants';
import { getColorFromKey, hyperlinkUrlsInText } from 'constants/helper';
import { PrivateViewIcon, StyledIcon, TwoPeopleIcon } from 'constants/icons';
import { ONE_ON_ONE_EVENTS_OLD, sendAddedToOneononeAgenda, sendOneOnOneEvent } from 'constants/mixpanelHelper';
import theme from 'constants/theme';

import { createAgenda, updateAgenda, getAgendas } from 'services/agenda';
import { showErrorSnackbar } from 'services/snackbar';

import { Typography4 } from 'uiToolkit/Typography';

import { TasksContainer } from '../sidepanelStyles';
import AgendaDisplay from './AgendaDisplay';
import useConsumeAgendaFromQueue from './useConsumeAgendaFromQueue';
import { usePagination } from './usePaginationAgenda';

const StyledImage = styled.img`
  width: 50%;
`;
const NoAgendaPluginView = () => (
  <VerticalFlexContainer
    style={{
      alignItems: 'center',
      justifyContent: 'flex-start',
      marginTop: '60px',
      padding: '0 24px',
    }}
  >
    <StyledImage src={NotAvailable} />
    <Typography4 topGutter={1} bottomGutter={3} customColor={theme.palette.text.label} align="center">
      You currently don't have any Agenda items,
      <br /> click here to add
    </Typography4>
  </VerticalFlexContainer>
);

const StyledSpaceBetweenContainer = styled(SpaceBetweenContainer)`
  align-items: center;
  ${(props) => props.theme.breakpoints.down('xs')} {
    padding-left: 24px;
    padding-right: 8px;
  }
`;

const AddAgendaButton = ({ onAddNewTask, isDisabled }) => {
  if (isDisabled) {
    return (
      <StyledTooltip title="Reopen this meeting to make changes">
        <CustomStyledAddAgendaButton
          style={{
            opacity: 0.5,
            cursor: 'default',
          }}
          variant="text"
          padding="0.5rem 1rem"
        >
          <CustomStyledIconButton
            circular
            color="primary"
            borderColor="primary"
            size="tinier"
            padding="xLarge"
            style={{ cursor: 'default' }}
          >
            <StyledAddIcon />
          </CustomStyledIconButton>
          <StyledTypography
            variant="body2"
            color="primary"
            leftGutter={1.5}
            align="center"
            style={{ lineHeight: 'normal' }}
          >
            Add Agenda
          </StyledTypography>
        </CustomStyledAddAgendaButton>
      </StyledTooltip>
    );
  }
  return (
    <CustomStyledAddAgendaButton variant="text" onClick={onAddNewTask} padding="0.5rem 1rem">
      <CustomStyledIconButton circular color="primary" borderColor="primary" size="tinier" padding="xLarge">
        <StyledAddIcon />
      </CustomStyledIconButton>
      <StyledTypography
        variant="body2"
        color="primary"
        leftGutter={1.5}
        align="center"
        style={{ lineHeight: 'normal' }}
      >
        Add Agenda
      </StyledTypography>
    </CustomStyledAddAgendaButton>
  );
};

const Agenda = ({
  header = 'Agenda',
  taskId: checkInInstanceId,
  subObjective, // for getting tasks in case of goal details
  teamId,
  // setTaskDetails,
  // useMentionFunctionality,
  parentTaskOwners: toIds = [],
  taskType: parentTaskType,
  match,
  parentCheckInId = null,
  filterEmpIds = null,
  className,
  questionId,
  visibility = 'PUBLIC',
  isDisabled,
  sourceContainer = APP_SOURCE_CONTAINERS.WEB_APP,
  isSubmitted = false,
}) => {
  const { pageName } = useMatchedRoute();
  const [agendaList, agendasDispatch] = useReducer(agendasReducer, []);

  const paramsEmployeeId = match.params.employeeId;
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const { page, hasMore, updatePagination, maxPageReached } = usePagination();
  const dispatch = useDispatch();
  const loggedInEmployee = useSelector(selectLoggedInEmployeeData, shallowEqual);
  const limit = 6;
  const isPlugin = sourceContainer === APP_SOURCE_CONTAINERS.PLUGIN;

  const { value: refetchAgendaList } = useSelector((state) => state.refetchAgendaList);

  const getData = () => {
    setLoading(true);
    let apiCall;
    const offset = page * limit;

    apiCall = getAgendas.bind(null, { checkInId: checkInInstanceId, limit, offset });
    apiCall()
      .then((agendas) => {
        agendasDispatch(addAgendasAction(agendas));
        updatePagination({
          hasMore: agendas.length === limit,
          page: page + 1,
          maxPageReached: page + 1,
        });
      })
      .catch((err) => {
        dispatch(showErrorSnackbar(err.message || 'Sorry! An error occurred while fetching agenda'));
        updatePagination({ hasMore: false });
      })
      .finally(() => setLoading(false));
  };

  const refetchData = () => {
    setLoading(true);
    let apiCall;
    const offset = 0 * limit;

    apiCall = getAgendas.bind(null, { checkInId: checkInInstanceId, limit, offset });
    apiCall()
      .then((agendas) => {
        agendasDispatch(updateAgendasAction(agendas));
        updatePagination({
          hasMore: agendas.length === limit,
          page: 1,
          maxPageReached: 1,
        });
      })
      .catch((err) => {
        dispatch(showErrorSnackbar(err.message || 'Sorry! An error occurred while fetching agenda'));
        updatePagination({ hasMore: false });
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!checkInInstanceId) return;
    getData();
  }, [checkInInstanceId]);

  useEffect(() => {
    updatePagination({ page: 0 });
    refetchData();
  }, [refetchAgendaList]);

  const prevAgendaListLength = usePrevious(agendaList?.length);

  useEffect(() => {
    if (prevAgendaListLength > agendaList?.length && agendaList?.length / (limit - 1) <= page - 1) {
      updatePagination({ page: page - 1 });
    }
  }, [agendaList?.length, page, limit, prevAgendaListLength]);

  const addAgendaItem = (agenda) => {
    setBusy(true);

    if (!agenda.text) {
      agendasDispatch(updateAgendaAction(agenda));
      setBusy(false);
      return;
    }

    // TODO: add new agenda creation event
    /* sendOneOnOneEvent({
        eventName: ONE_ON_ONE_EVENTS_OLD.addedActionItem,
        page: {
          pageName,
          flow: 'Activity'
        },
        secondaryAction: 'addedAgenda'
      }); */

    createAgenda({
      ...agenda,
      checkInInstanceId,
      questionId,
    })
      .then((createdAgenda) => {
        createdAgenda.isEditing = false;
        // Making sure that if this was a new agenda, it has a key of that one
        createdAgenda.uiKey = agenda.uiKey;
        // getData();
        agendasDispatch(updateAgendaAction(createdAgenda));
      })
      .catch((err) => dispatch(showErrorSnackbar(err.message || 'Sorry! An error occured while creating the agenda')))
      .finally(() => setBusy(false));
  };

  const updateAgendaItem = (agenda) => {
    setBusy(true);

    if (!agenda.text) {
      agendasDispatch(updateAgendaAction(agenda));
      setBusy(false);
      return;
    }

    // TODO: fix tracking event
    /* 
      sendOneOnOneEvent({
        eventName: ONE_ON_ONE_EVENTS_OLD.addedActionItem,
        page: {
          pageName,
          flow: 'Activity'
        },
        secondaryAction: 'addedAgenda'
      });
    */

    updateAgenda({
      ...agenda,
      agendaId: agenda.uuid,
      questionId,
      checkInInstanceId,
    })
      .then((updatedAgenda) => {
        updatedAgenda.isEditing = false;
        updatedAgenda.uiKey = agenda.uiKey;
        agendasDispatch(updateAgendaAction(updatedAgenda));
      })
      .catch((err) => dispatch(showErrorSnackbar(err.message || 'Sorry! An error occured while creating the agenda')))
      .finally(() => setBusy(false));
  };

  const createOrUpdate = (agenda) => {
    if (!agenda.uuid) {
      addAgendaItem(agenda);
    } else {
      updateAgendaItem(agenda);
    }
  };

  const onCheckToggle = async (agenda) => {
    const isAlreadyCompleted = Boolean(agenda.status === AGENDA_STATUSES.completed);

    createOrUpdate({
      ...agenda,
      status: isAlreadyCompleted ? AGENDA_STATUSES.active : AGENDA_STATUSES.completed,
    });

    // Send Mark Complete or revertComplete
    sendOneOnOneEvent({
      eventName: ONE_ON_ONE_EVENTS_OLD.updateAgendaItem,
      page: {
        pageName,
        flow: 'Activity',
      },
      secondaryAction: isAlreadyCompleted ? 'revertCompletedAgenda' : 'completedAgenda',
    });
  };

  const onDeleteAgenda = (id, key) => {
    setBusy(true);

    agendasDispatch(removeAgendaAction(id, key));

    setBusy(false);
  };

  const onAddNewTask = useCallback(() => {
    updatePagination({ page: 1 });
    agendasDispatch(addNewAgendaAction(loggedInEmployee));
    sendAddedToOneononeAgenda({
      eventProperties: {
        role: loggedInEmployee.userRole,
        type: 'None',
      },
    });
  }, [JSON.stringify(loggedInEmployee)]);

  // const completedTasks = agendaList.filter(agenda => agenda.isComplete());
  // const activeTasks = agendaList.filter(agenda => !agenda.isComplete());

  const visibleAgendas = agendaList.slice((page - 1) * 5, 5 + (page - 1) * 5);

  const hasMoreToDisplay = hasMore || agendaList.length > 5 + (page - 1) * 5;

  const safeHeader = DOMPurify.sanitize(hyperlinkUrlsInText(header));

  const AgendaHeader = ({ isPlugin, disabledAgendaCreation }) => (
    <StyledSpaceBetweenContainer bottomGutter={1}>
      <StyledGrid style={{ display: 'flex' }} alignItems="flex-start" wrap="nowrap">
        <StyledIcon
          size={isPlugin ? 'fixed14px' : 'tinier'}
          topGutter={0.5}
          rightGutter={isPlugin ? '1.5' : '1'}
          color={theme.palette.text.heading}
        >
          {visibility === 'PUBLIC' ? (
            <TwoPeopleIcon />
          ) : (
            <StyledTooltip title="Only you can see this agenda">
              <PrivateViewIcon />
            </StyledTooltip>
          )}
        </StyledIcon>

        <StyledTypography display="inline" variant="body2" weight={isPlugin ? '400' : '600'} wordBreak>
          {parse(safeHeader)}
        </StyledTypography>
      </StyledGrid>
      {/* <FlexContainer style={{ cursor: 'pointer', alignItems: 'center' }} onClick={onAddNewTask}> */}
      <AddAgendaButton isDisabled={disabledAgendaCreation} onAddNewTask={onAddNewTask} />
      {/* </FlexContainer> */}
    </StyledSpaceBetweenContainer>
  );

  useConsumeAgendaFromQueue({
    agendasDispatch,
    setBusy,
    checkInInstanceId,
    questionId,
    updatePagination,
  });

  useEffect(() => {
    if (isSubmitted) dispatch(disableAgendaQueueAction());
    else dispatch(enableAgendaQueueAction());
    return () => dispatch(disableAgendaQueueAction());
  }, [isSubmitted]);

  if (loading) {
    return (
      <>
        <AgendaHeader isPlugin={isPlugin} />
        <Skeleton width="40%" />
        <Skeleton width="50%" />
      </>
    );
  }

  return (
    <TasksContainer className={className} disabled={busy}>
      {agendaList && (
        <>
          {parentTaskType === TASK_TYPES_2.checkIn && (
            <AgendaHeader disabledAgendaCreation={isDisabled} isPlugin={isPlugin} />
          )}

          {!loading &&
            visibleAgendas.length === 0 &&
            (isPlugin ? (
              <>
                <NoAgendaPluginView />
                <FlexContainer style={{ justifyContent: 'center' }}>
                  <AddAgendaButton onAddNewTask={onAddNewTask} />
                </FlexContainer>
              </>
            ) : (
              <NoActionPointsView line1="Add the agenda for your next 1:1" line2="" />
            ))}

          <StyleOverrideWrapper footerVisible={agendaList.length >= 5} isPlugin={isPlugin}>
            {visibleAgendas.map((agenda, index) => (
              <AgendaDisplay
                key={`agenda-${agenda.uuid || `new${index}`}`}
                agenda={agenda}
                onCheckToggle={onCheckToggle}
                onDelete={onDeleteAgenda}
                onUpdate={createOrUpdate}
                agendasDispatch={agendasDispatch}
                teamId={teamId}
                filterEmpIds={filterEmpIds}
                isPlugin={isPlugin}
              />
            ))}
            {agendaList.length > limit - 1 && (
              <Footer>
                <div />
                <StyledTablePagination
                  component="div"
                  labelDisplayedRows={() => ''} // for hiding page count
                  page={page - 1}
                  rowsPerPageOptions={[]}
                  onChangePage={(e, newPage) => {
                    if (newPage < page) {
                      // user goes to previous
                      updatePagination({ page: newPage + 1, hasMore: true });
                    } else if (maxPageReached < newPage + 1) {
                      // user goes forward and is seeing page for first time
                      getData();
                    } else {
                      // user goes forward and is seeing page again
                      updatePagination({ page: newPage + 1 });
                    }
                  }}
                  nextIconButtonProps={{ disabled: !hasMoreToDisplay || loading }}
                />
              </Footer>
            )}
          </StyleOverrideWrapper>
        </>
      )}
    </TasksContainer>
  );
};

Agenda.propTypes = {
  parentTaskId: PropType.number.isRequired,
  parentTaskType: PropType.string.isRequired,
};

export default withRouter(Agenda);

const NewTaskButton = styled(StyledButton)`
  ${MeshOutlineButtonCSS};
`;

const CustomStyledIconButton = styled(StyledIconButton)`
  &.MuiIconButton-root.Mui-disabled {
    background-color: ${getColorFromKey('primary')};
  }
  &.MuiButtonBase-root {
    width: 24px;
    height: 24px;
  }
`;

const CustomStyledAddAgendaButton = styled(StyledButton)`
  &: hover {
    background-color: ${(props) => props.theme.palette.background.lightBlue};
  }
`;

const StyledAddIcon = styled(AddIcon)`
  width: 16px;
  height: 16px;
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  // margin-top: 1rem;
  // margin-bottom: 5rem;
`;

const StyleOverrideWrapper = styled.div`
  margin: 1.5rem 0.25rem;

  ${(props) =>
    !props.isPlugin &&
    css`
      > div {
        margin: 1.5rem 0;
        padding: 0.25rem 0.5rem 0.25rem 1rem;
      }
    `}
  ${(props) =>
    props.footerVisible &&
    !props.isPlugin &&
    css`
      > div:nth-last-child(2) {
        margin: 1.5rem 0 0 0;
        padding: 0.25rem 0.5rem 0.25rem 1rem;
      }
    `}
  ${Footer} {
    margin: 0 3.2rem 0 0;
    padding: 0;
    ${(props) => props.theme.breakpoints.down('xs')} {
      margin-right: 16px;
    }
  }

  border: 1px solid ${(props) => props.theme.palette.borders.lighterGrey};
  ${(props) => props.theme.breakpoints.down('xs')} {
    border: 0px;
    margin: 24px 0;
  }
  border-radius: 0.25rem;
`;

const StyledTablePagination = styled(TablePagination)`
  .MuiTablePagination-root {
    margin: 0 3.2rem 0 0;
    ${(props) => props.theme.breakpoints.down('xs')} {
      margin: 0;
    }
  }

  // .MuiTablePagination-caption {
  //   padding: 1rem 1.5rem;
  // }
  .MuiTablePagination-actions button {
    background: ${(props) => props.theme.palette.background.paper};
    border-radius: 0;
    width: 24px;
    height: 24px;
  }
`;
